import React, { useEffect, useState, useCallback, useMemo } from "react";
import { HiChevronDown, HiFilter } from "react-icons/hi";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import queryString from "query-string";
import Header from "src/layouts/Header";
import Footer from "src/layouts/Footer";
import ArticleList from "src/components/section/ArticleList";
import Pagination from "src/components/common/Pagination";
import axios from "src/api/axios";
import { NotificationManager } from "react-notifications";
import noFavorites from "src/assets/svg/nofavourites.svg";
import ColorBox from 'src/components/ColorBox'
import PriceFilter from 'src/components/PriceFilter';
import useSEO from 'src/hooks/useSEO';
import { generateSearchTitle, generateSearchDescription, generateSearchKeywords } from 'src/utils/searchSEO';

const Search = () => {
  const navigate = useNavigate();
  const [adOptions, setAdOptions] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState([]);
  const [showAllFilters, setShowAllFilters] = useState(false);

  // SEO - dynamically generated based on search filters
  const seoData = useMemo(() => {
    const hasFilters = Array.from(searchParams.keys()).some(key => !['page', 'sort_by'].includes(key));
    
    if (!hasFilters) {
      return {
        title: 'Search Sphynx Cats - SphynxPlace.com',
        description: 'Search for Sphynx cats, breeders, and adoption listings on SphynxPlace.com. Find your perfect Sphynx companion with advanced search filters.',
        keywords: 'search Sphynx cats, find Sphynx, Sphynx listings, cat search',
        url: window.location.href,
      };
    }

    const title = generateSearchTitle(searchParams, adOptions);
    const description = generateSearchDescription(searchParams, adOptions, meta?.total);
    const keywords = generateSearchKeywords(searchParams, adOptions);

    return {
      title,
      description,
      keywords,
      url: window.location.href,
    };
  }, [searchParams, adOptions, meta?.total]);

  useSEO(seoData);

  useEffect(() => {
    const openDropdowns = Array.from(searchParams.keys());
    setOpenDropdowns(openDropdowns);
  }, []);

  const handleChange = (e) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(e.target.name, e.target.value);
    setSearchParams(newParams);
  }

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const params = Object.fromEntries(searchParams.entries());

        // Convert comma-separated values into array format for query string
        const queryParams = new URLSearchParams();

        // Handle array parameters
        ['color', 'age', 'compatibility', 'health-and-documentation'].forEach(param => {
          if (params[param]) {
            params[param].split(',').forEach(value => {
              queryParams.append(`${param}[]`, value);
            });
          }
        });

        // Add non-array parameters
        Object.entries(params).forEach(([key, value]) => {
          if (!['age', 'color', 'compatibility', 'health-and-documentation'].includes(key)) {
            queryParams.append(key, value);
          }
        });

        const response = await axios.get("/api/search/articles?" + queryParams.toString());
        setResults(response.data);
        setArticles(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [searchParams]);

  useEffect(() => {
    fetchAdOptions();
  }, [searchParams]);

    const fetchAdOptions = useCallback(async () => {
    try {
      const response = await axios.get(`/api/resources/search${searchParams.get('ad_type') ? `?ad_type=${searchParams.get('ad_type')}` : ''}`)
      setAdOptions(response.data.data)
      // Reset filters that don't have corresponding fields in the response
      const newParams = new URLSearchParams(searchParams);
      const availableFilters = Object.keys(response.data.data);
      ['color', 'age', 'gender', 'compatibility', 'health-and-documentation', 'advertiser-type', 'price_from', 'price_to'].forEach(filter => {
        if (!availableFilters.includes(filter) && newParams.has(filter)) {
          // Za price filtere, provjeri da li postoji price-range
          if ((filter === 'price_from' || filter === 'price_to') && !availableFilters.includes('price-range')) {
            newParams.delete(filter);
          } else if (filter !== 'price_from' && filter !== 'price_to') {
            newParams.delete(filter);
          }
        }
      });

      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams);
      }
    } catch (error) {
      NotificationManager.error('Error fetching ad options')
    }
  }, [searchParams.get('ad_type')])

  const handleChangeFilter = (e) => {
    // Ignorišemo promene koje dolaze od price inputa
    if (e.target.id === 'price_from' || e.target.id === 'price_to') {
      return;
    }
    
    // Ostatak logike za ostale filtere
    const { name, value, type } = e.target;
    const newParams = new URLSearchParams(searchParams);
    
    if (type === 'checkbox') {
      let values = searchParams.get(name)?.split(',') || [];
      
      if (e.target.checked) {
        values.push(value);
      } else {
        values = values.filter(v => v !== value);
      }
      
      if (values.length) {
        const otherParams = Array.from(searchParams.entries())
          .filter(([key]) => key !== name)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
          
        const param = `${name}=${values.join(',')}`;
        const query = otherParams ? `${param}&${otherParams}` : param;
        
        navigate(`/search?${query}`);
        return;
      } else {
        newParams.delete(name);
      }
    } else {
      if (value) {
        newParams.set(name, value);
      } else {
        newParams.delete(name);
      }
    }
    
    setSearchParams(newParams);
  };

  const handlePriceFilter = (prices) => {
    const newParams = new URLSearchParams(searchParams);
    if (prices.price_from) {
      newParams.set('price_from', prices.price_from);
    } else {
      newParams.delete('price_from');
    }
    if (prices.price_to) {
      newParams.set('price_to', prices.price_to);
    } else {
      newParams.delete('price_to');
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    setSearchParams(prev => ({...Object.fromEntries(prev.entries()), page}));
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set('sort_by', value);
      } else {
        newParams.delete('sort_by');
      }
      return newParams;
    });
  };

  // Sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    document.body.classList.toggle('overflow-hidden');
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.classList.toggle('hidden');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = document.getElementById('sidebar-multi-level-sidebar');
      if (sidebar && !sidebar.contains(event.target) && sidebarOpen) {
        setSidebarOpen(false);
        document.body.classList.remove('overflow-hidden');
        const overlay = document.getElementById('sidebar-overlay');
        if (overlay) {
          overlay.classList.add('hidden');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  // dropdown
  useEffect(() => {
    // Check which dropdowns should be visible initially based on checked elements
    const dropdowns = document.querySelectorAll('.dropdown-content');
    dropdowns.forEach((dropdown, index) => {
      const checkedElements = dropdown.querySelectorAll('input:checked');
      if (checkedElements.length > 0) {
        dropdown.classList.remove('hidden');
      }
    });
  }, []);
  
  const toggleDropdown = (name) => {
    const dropdown = document.querySelector(`[data-dropdown="${name}"]`);
    if (dropdown) {
      dropdown.classList.toggle('hidden');
    }
  };

  // Add this function to reset all form inputs
  const resetFormInputs = useCallback(() => {
    const form = document.querySelector('form');
    if (form) {
      const inputs = form.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      inputs.forEach(input => {
        input.checked = false;
      });
    }
  }, []);

  // Add effect to handle URL changes and reset form when needed
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (params.toString() === '') {
      resetFormInputs();
    }
  }, [searchParams, resetFormInputs]);

  // Add this helper function to get readable filter names
  const getFilterLabel = (key, value) => {
    // Special case for ad_type
    if (key === 'ad_type') {
      return `${adOptions[key]?.options[value] || value}`;
    }
    
    if (!adOptions[key]) return value;
    return `${key.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')}: ${adOptions[key].options[value] || value}`;
  };

  // Add this function to remove individual filters
  const removeFilter = (key) => {
    const newParams = new URLSearchParams(searchParams);
    if (key === 'price') {
      newParams.delete('price_from');
      newParams.delete('price_to');
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Update the getActiveFilters function
  const getActiveFilters = () => {
    const filters = [];
    const groupedFilters = {};

    for (const [key, value] of searchParams.entries()) {
      // Skip pagination and sorting params
      if (['page', 'sort_by'].includes(key)) continue;
      
      // Group price filters
      if (key === 'price_from' || key === 'price_to') {
        if (!groupedFilters.price) {
          groupedFilters.price = { from: null, to: null };
        }
        if (key === 'price_from') {
          groupedFilters.price.from = value;
        } else {
          groupedFilters.price.to = value;
        }
        continue;
      }

      // Group values by key for array parameters
      if (value.includes(',')) {
        const values = value.split(',');
        // Remove the key name from individual values for array parameters
        const formattedValues = values.map(v => adOptions[key]?.options[v] || v);
        
        // Format the label with the key name only once
        const label = key === 'ad_type' 
          ? `Type of Listing: ${formattedValues.join(', ')}`
          : `${key.split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')}: ${formattedValues.join(', ')}`;

        filters.push({
          key,
          value,
          label
        });
      } else {
        filters.push({
          key,
          value,
          label: key === 'ad_type' 
            ? `Type of Listing: ${getFilterLabel(key, value)}` 
            : getFilterLabel(key, value)
        });
      }
    }

    // Add price filter if exists
    if (groupedFilters.price) {
      const { from, to } = groupedFilters.price;
      if (from || to) {
        filters.push({
          key: 'price',
          value: `${from || ''}-${to || ''}`,
          label: `Price: ${from ? `from $${from}` : ''}${from && to ? ' - ' : ''}${to ? `to $${to}` : ''}`
        });
      }
    }
    
    return filters;
  };

  return (
    <>
      <Header />
      <div className='bg-[#F7F7F7] py-[20px] md:py-[35px]'>
        <div className='container mx-auto px-2 relative md:flex md:gap-[15px] 2xl:gap-[32px] md:justify-between'>
          <aside id="sidebar-multi-level-sidebar" className={`fixed h-screen md:max-h-[85vh] flex flex-col md:sticky top-0 left-0 z-40 w-66 transition-transform ${!sidebarOpen ? '-translate-x-full' : ''} md:translate-x-0 md:min-w-[330px] md:rounded-lg overflow-hidden`} aria-label="Sidebar">
            <div className='flex gap-4 px-6 py-4 justify-between bg-[#832EE6] text-white items-center'>
              <h3 className='font-bold text-xl'>Filters</h3>
              <Link to="/search">Clear all</Link>
            </div>
            <div className="h-full px-3 py-4 overflow-y-auto bg-white dark:bg-gray-800">
              <form action="" onChange={handleChangeFilter}>
                <ul className="space-y-2 font-medium">
                  {/* 1) Type of Listing */}
                  <li>
                    <button type="button" className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Type of Listing</span>
                    </button>
                    <ul data-dropdown="type" className={`dropdown-content py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions?.ad_type?.options || {}).map(([value, label], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                          <input name='ad_type' defaultChecked={searchParams.get('ad_type') === value ? true : false} id={`ad-type-${index}`} type="radio" value={value} className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600" />
                          <label htmlFor={`ad-type-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{label}</label>
                        </div>
                      </li>
                      ))}
                    </ul>
                  </li>

                  {/* 2) Price - ne prikazuje se za For Adoption */}
                  {adOptions?.['price-range'] && (
                  <li>
                    <button type="button" className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Price</span>
                    </button>
                    <ul className="py-2 pl-4 space-y-2">
                      <li>
                        <PriceFilter 
                          initialFrom={searchParams.get('price_from')}
                          initialTo={searchParams.get('price_to')}
                          onFilter={handlePriceFilter}
                        />
                      </li>
                    </ul>
                  </li>
                  )}

                  {/* 3) Cat Color */}
                  {adOptions?.color?.options && (
                    <li>
                      <button type="button" className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                        <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Cat Color</span>
                      </button>
                      <ul data-dropdown="color" className={`dropdown-content py-2 pl-4 grid grid-cols-2 gap-2`}>
                        {Object.entries(adOptions.color.options).map(([key, value], index) => (
                          <ColorBox key={index} name="color" type="checkbox" value={key} spanText={value} onChange={handleChange} defaultChecked={searchParams.get('color') === key} />
                        ))}
                      </ul>
                    </li>
                  )}

                  {/* 4) Age */}
                  {adOptions?.age?.options && (
                    <li>
                      <button type="button" className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Age</span>
                    </button>
                    <ul data-dropdown="age" className={`dropdown-content py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions.age.options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                            <input 
                            name={adOptions.age.slug} 
                            defaultChecked={searchParams.get(adOptions.age.slug)?.split(',').includes(key)} 
                            id={`age-checkbox-${index}`} 
                            type={adOptions.age.type}
                            value={key}
                            className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600"
                          />
                          <label htmlFor={`age-checkbox-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{value}</label>
                        </div>
                      </li>
                      ))}
                    </ul>
                  </li>
                  )}

                  {/* 5) Gender - ne prikazuje se za For Stud */}
                  {adOptions?.gender?.options && (
                  <li>
                    <button type="button" onClick={() => toggleDropdown('gender')} className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Gender</span>
                      <HiChevronDown />
                    </button>
                    <ul data-dropdown="gender" className={`dropdown-content ${openDropdowns.includes('gender') ? '' : 'hidden'} py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions.gender.options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                          <input name="gender" value={key} defaultChecked={searchParams.get('gender') === key ? true : false} id={`gender-checkbox-${index}`} type="radio" className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600" />
                          <label htmlFor={`gender-checkbox-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{value}</label>
                        </div>
                      </li>
                      ))}
                    </ul>
                  </li>
                  )}

                  {/* 6) Compatibility */}
                  {adOptions?.compatibility?.options && (
                  <li>
                    <button type="button" onClick={() => toggleDropdown('compatibility')} className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Compatibility</span>
                      <HiChevronDown />
                    </button>
                    <ul data-dropdown="compatibility" className={`dropdown-content ${openDropdowns.includes('compatibility') ? '' : 'hidden'} py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions.compatibility.options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                            <input 
                              name={adOptions.compatibility.slug} 
                              value={key}
                              defaultChecked={searchParams.get(adOptions.compatibility.slug)?.split(',').includes(key)}
                              id={`compatibility-checkbox-${index}`}
                              type={adOptions.compatibility.type}
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor={`compatibility-checkbox-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{value}</label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                  )}

                  {/* 7) Health & Documentation */}
                  {adOptions?.['health-and-documentation']?.options && (
                  <li>
                    <button type="button" onClick={() => toggleDropdown('health-and-documentation')} className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Health & Documentation</span>
                      <HiChevronDown />
                    </button>
                    <ul data-dropdown="health-and-documentation" className={`dropdown-content ${openDropdowns.includes('health-and-documentation') ? '' : 'hidden'} py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions['health-and-documentation'].options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                            <input 
                              name="health-and-documentation"
                              value={key}
                              defaultChecked={searchParams.get('health-and-documentation')?.split(',').includes(key)}
                              id={`health-checkbox-${index}`}
                              type="checkbox"
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor={`health-checkbox-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{value}</label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                  )}

                  {/* 8) Advertiser Type */}
                  {adOptions?.['advertiser-type']?.options && (
                  <li>
                    <button type="button" onClick={() => toggleDropdown('advertiser-type')} className="flex items-center w-full p-2 text-base text-gray-900 transition duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700">
                      <span className="flex-1 ml-3 text-left whitespace-nowrap font-bold">Advertiser Type</span>
                      <HiChevronDown />
                    </button>
                    <ul data-dropdown="advertiser-type" className={`dropdown-content ${openDropdowns.includes('advertiser-type') ? '' : 'hidden'} py-2 pl-4 space-y-2`}>
                      {Object.entries(adOptions['advertiser-type'].options).map(([key, value], index) => (
                        <li key={index}>
                          <div className="flex items-center mr-4">
                            <input 
                              name="advertiser-type" 
                              value={key}
                              defaultChecked={searchParams.get('advertiser-type') === key ? true : false}
                              id={`advertiser-type-checkbox-${index}`}
                              type="radio"
                              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 accent-purple-600 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label htmlFor={`advertiser-type-checkbox-${index}`} className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">{value}</label>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                  )}
                </ul>
              </form>
              {getActiveFilters().length > 0 && (
                <div className="px-3 pb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => navigate('/search')}
                    className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </aside>
          <div className="w-full">
              <div className='flex gap-[15px] 2xl:gap-[32px] justify-between mb-2'>
                <button onClick={toggleSidebar} type="button" className="inline-flex items-center h-[43px] py-2 px-4 text-sm text-gray-500 md:hidden rounded-lg bg-white hover:bg-gray-100 focus:outline-none border  border-gray-300 accent-purple-600 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600">
                  <span className="sr-only">Open sidebar</span>
                  <HiFilter className='w-4 h-4 mr-2' />
                  <span className="text-black">Filters</span>
                </button>
                <p className='my-auto hidden md:block'>
                  <span> {loading ? '?' : meta.total}</span> Sphynxs
                </p>
                <div>
                  <select 
                    id="sort" 
                    value={searchParams.get('sort_by') || ''}
                    onChange={handleSortChange}
                    className="bg-gray-50 border border-gray-300 text-sm md:text-base block w-full md:w-[170px] p-2.5 rounded dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  >
                    <option value="">Sort by</option>
                    <option value="price_asc">Price (low to high)</option>
                    <option value="price_desc">Price (high to low)</option>
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                  <p className='my-auto md:hidden text-right pt-2'>
                    <span>{meta.total}</span> Sphynxs
                  </p>
                </div>

              </div>

              {/* Update the active filters section with improved mobile styling */}
              {getActiveFilters().length > 0 && (
                <div className="mb-4">
                  <div className="relative">
                    <div className={`flex flex-wrap gap-2 mb-2 ${!showAllFilters && 'overflow-hidden md:overflow-visible h-[32px] md:h-auto'}`}>
                      {getActiveFilters().map((filter, index) => (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm bg-purple-100 text-purple-800 ${
                            !showAllFilters && index >= 3 ? 'hidden md:inline-flex' : ''
                          }`}
                        >
                          {filter.label}
                          <button
                            onClick={() => removeFilter(filter.key)}
                            className="ml-1 md:ml-2 hover:text-purple-900 p-1"
                            aria-label="Remove filter"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {getActiveFilters().length >= 3 && (
                        <button
                          onClick={() => setShowAllFilters(!showAllFilters)}
                          className="text-xs text-purple-600 hover:text-purple-800 underline md:hidden"
                        >
                          {showAllFilters ? 'Show less' : `Show more`}
                        </button>
                      )}
                      <button
                        onClick={() => navigate('/search')}
                        className="text-xs md:text-sm text-purple-600 hover:text-purple-800 underline font-medium"
                      >
                        Clear all filters
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex items-center justify-center py-6 md:py-10 min-h-[50vh]">
                  <div className="w-12 h-12 border-4 border-purple-600 rounded-full animate-spin border-t-transparent"></div>
                </div>
              ) : (
                <div className='flex flex-col'>
                  {articles.length > 0 ? (
                    articles.map(article => (
                      <ArticleList key={article.id} article={article} />
                    ))
                  ) : (
                    <div className='flex flex-col'>
                    <img src={noFavorites} alt="nofavorites" className="mx-auto mt-5 md:mt-10 w-[200px] md:w-auto"/>
                    <h2 className="uppercase font-bold text-[20px] md:text-[24px] leading-[28px] md:leading-[35px] my-[30px] md:my-[50px] text-center">No results found</h2>
                      <Link to="/search" className="bg-[#832EE6] hover:bg-purple-800 text-white px-[30px] md:px-[45px] mx-auto py-[12px] md:py-[16px] text-[16px] md:text-[18px] leading-5 md:leading-6 font-bold inline-block rounded-[27px]">Clear filters</Link>
                    </div>
                  )}
                  {meta?.total > 0 && meta?.last_page > 1 && <Pagination meta={meta} onPageChange={handlePageChange} />}
                </div>
              )}

           </div>
        </div>
      </div>
      <div id='sidebar-overlay' className='bg-gray-900/50 dark:bg-gray-900/80 fixed hidden inset-0 z-30'></div>
      <Footer />
    </>
  )
}

export default Search